/**
 * MessageRating — feedback on an answer, positive included.
 *
 * The rule (2026-09-29):
 *   - A thumb click RECORDS the rating at once — a rating without words stays
 *     one click. The same click opens a small popover anchored to the thumbs
 *     that refines it: quick reasons for that polarity, an optional comment,
 *     and Send. Closing it without sending keeps the rating.
 *   - Both polarities take reasons and a comment. Reasons come from
 *     config/message_feedback_reasons.json, the one definition the backend
 *     validates against; their labels are chat.rating.reasons.<key>.
 *   - Send is acknowledged in place ("Thanks — this helps improve <agent>")
 *     with a check that draws itself (static under prefers-reduced-motion),
 *     then the popover closes. The filled thumb stays on the message.
 *   - Pressing the active thumb re-opens the popover to change the reasons or
 *     comment, switch polarity, or remove the rating.
 *   - Never a modal: Escape closes, Enter sends (Shift+Enter is a new line),
 *     a click outside closes, focus returns to the thumb.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, Tooltip } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { mirrorPlacement } from '@/utils/rtl';
import reasonCatalog from '../../../../../config/message_feedback_reasons.json';
import './MessageRating.css';

export type Rating = 'like' | 'dislike';

export interface MessageFeedback {
  rating: Rating | null;
  reasons: string[];
  feedback_description: string | null;
}

interface MessageRatingProps {
  messageId: string;
  conversationId: string;
  initialRating?: Rating | null;
  initialReasons?: string[] | null;
  initialDescription?: string | null;
  /** The agent that answered — named in the thank-you. */
  agentName?: string;
  onFeedbackChange?: (messageId: string, feedback: MessageFeedback) => void;
}

const REASON_KEYS: Record<Rating, string[]> = {
  like: reasonCatalog.like.map((r) => r.key),
  dislike: reasonCatalog.dislike.map((r) => r.key),
};

const THANKS_MS = 1800;

function ThumbIcon({ down, filled }: { down?: boolean; filled: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {down
        ? <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
        : <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />}
    </svg>
  );
}

export function MessageRating({
  messageId,
  conversationId,
  initialRating,
  initialReasons,
  initialDescription,
  agentName,
  onFeedbackChange,
}: MessageRatingProps) {
  const { t, direction } = useLanguage();

  // What is saved on the server.
  const [saved, setSaved] = useState<MessageFeedback>({
    rating: initialRating ?? null,
    reasons: initialReasons ?? [],
    feedback_description: initialDescription ?? null,
  });
  // The conversation's feedback map is the source of truth: it loads after
  // mount, and every change published here flows back through it.
  useEffect(() => {
    if (initialRating === undefined) return;
    setSaved({
      rating: initialRating ?? null,
      reasons: initialReasons ?? [],
      feedback_description: initialDescription ?? null,
    });
  }, [initialRating, initialReasons, initialDescription]);

  const [open, setOpen] = useState(false);
  // Which thumb the pointer is over. The tooltip is driven by this alone:
  // left to its own hover state, antd's Tooltip missed the mouseleave while
  // the popover had hidden it (title null) and came back stuck open after
  // the popover closed — the label stayed over the thumbs (2026-09-29).
  const [hovered, setHovered] = useState<Rating | null>(null);
  const [phase, setPhase] = useState<'edit' | 'thanks'>('edit');
  const [draftReasons, setDraftReasons] = useState<string[]>([]);
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const thumbRefs = { like: useRef<HTMLButtonElement>(null), dislike: useRef<HTMLButtonElement>(null) };
  const thanksTimer = useRef<number | null>(null);

  // The popover renders in a portal, outside `.conversation-panel` where the
  // agent's colour tokens live — carry them across so its chips wear the
  // same agent tint as the composer's.
  const [agentTokens, setAgentTokens] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const cs = getComputedStyle(anchorRef.current);
    const tokens: Record<string, string> = {};
    for (const name of ['--agent-hue', '--agent-soft']) {
      const value = cs.getPropertyValue(name).trim();
      if (value) tokens[name] = value;
    }
    setAgentTokens(tokens as React.CSSProperties);
  }, [open]);

  const publish = useCallback((fb: MessageFeedback) => {
    setSaved(fb);
    onFeedbackChange?.(messageId, fb);
  }, [messageId, onFeedbackChange]);

  const close = useCallback((returnFocus = true) => {
    if (thanksTimer.current) window.clearTimeout(thanksTimer.current);
    thanksTimer.current = null;
    setOpen(false);
    setPhase('edit');
    setError(false);
    if (returnFocus && saved.rating) thumbRefs[saved.rating].current?.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved.rating]);

  useEffect(() => () => {
    if (thanksTimer.current) window.clearTimeout(thanksTimer.current);
  }, []);

  // Click outside (not on the thumbs, not in the popover) closes; so does Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const url = `/conversations/${conversationId}/messages/${messageId}/rating`;

  const post = async (body: Record<string, unknown>): Promise<MessageFeedback | null> => {
    setSaving(true);
    setError(false);
    try {
      const resp = await api.post(url, body);
      const fb = resp.data.feedback;
      const next: MessageFeedback = {
        rating: fb.rating,
        reasons: fb.reasons ?? [],
        feedback_description: fb.feedback_description ?? null,
      };
      publish(next);
      return next;
    } catch (err) {
      console.error('Failed to save message feedback:', err);
      setError(true);
      return null;
    } finally {
      setSaving(false);
    }
  };

  /** Thumb (under the message or in the popover): record now, then refine. */
  const chooseRating = async (rating: Rating) => {
    if (saving) return;
    if (saved.rating === rating) {
      // Already rated this way: open (or close) the popover to edit.
      if (open) { close(); return; }
      setDraftReasons(saved.reasons);
      setDraftComment(saved.feedback_description ?? '');
      setPhase('edit');
      setOpen(true);
      return;
    }
    const prev = saved;
    // Optimistic — the thumb fills at once.
    publish({ ...saved, rating, reasons: [] });
    setDraftReasons([]);
    setDraftComment(saved.feedback_description ?? '');
    setPhase('edit');
    setOpen(true);
    const next = await post({ rating });
    if (!next) publish(prev);
  };

  const toggleReason = (key: string) => {
    setDraftReasons((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const send = async () => {
    if (saving || !saved.rating) return;
    const next = await post({ rating: saved.rating, reasons: draftReasons, comment: draftComment });
    if (!next) return;
    setPhase('thanks');
    thanksTimer.current = window.setTimeout(() => close(), THANKS_MS);
  };

  const remove = async () => {
    if (saving) return;
    const prev = saved;
    publish({ rating: null, reasons: [], feedback_description: null });
    setOpen(false);
    setSaving(true);
    try {
      await api.delete(url);
    } catch (err) {
      console.error('Failed to remove message rating:', err);
      publish(prev);
    } finally {
      setSaving(false);
    }
  };

  const onCommentKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  const rating = saved.rating;
  const polarity: Rating = rating ?? 'like';

  const panel = (
    <div
      ref={panelRef}
      className={`rating-panel ${polarity}`}
      style={agentTokens}
      role="dialog"
      aria-label={t(polarity === 'like' ? 'chat.rating.likeTitle' : 'chat.rating.dislikeTitle')}
    >
      {phase === 'thanks' ? (
        <div className="rating-thanks" role="status" aria-live="polite">
          <svg className="rating-thanks-check" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M7.5 12.5l3 3 6-6.5" />
          </svg>
          <span>
            {agentName ? t('chat.rating.thanks', { name: agentName }) : t('chat.rating.thanksGeneric')}
          </span>
        </div>
      ) : (
        <>
          <div className="rating-panel-head">
            <span className="rating-panel-title">
              {t(polarity === 'like' ? 'chat.rating.likeTitle' : 'chat.rating.dislikeTitle')}
            </span>
            <div className="rating-panel-polarity" role="group" aria-label={t('chat.rating.edit')}>
              {(['like', 'dislike'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`rating-polarity-btn ${r} ${rating === r ? 'active' : ''}`}
                  aria-pressed={rating === r}
                  aria-label={t(r === 'like' ? 'chat.rating.like' : 'chat.rating.dislike')}
                  title={t(r === 'like' ? 'chat.rating.like' : 'chat.rating.dislike')}
                  onClick={() => { if (rating !== r) void chooseRating(r); }}
                  disabled={saving}
                >
                  <ThumbIcon down={r === 'dislike'} filled={rating === r} />
                </button>
              ))}
            </div>
          </div>

          <div className="rating-chips" role="group" aria-label={t('chat.rating.reasonsLabel')}>
            {REASON_KEYS[polarity].map((key, i) => {
              const on = draftReasons.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`rating-chip ${on ? 'on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleReason(key)}
                  // First chip takes focus on open so the keyboard lands inside.
                  autoFocus={i === 0}
                >
                  {t(`chat.rating.reasons.${key}`)}
                </button>
              );
            })}
          </div>

          <label className="rating-comment-label">
            <span>{t('chat.rating.commentLabel')}</span>
            <textarea dir="auto"
              className="rating-comment"
              value={draftComment}
              onChange={(e) => setDraftComment(e.target.value)}
              onKeyDown={onCommentKey}
              placeholder={t(polarity === 'like' ? 'chat.rating.likePlaceholder' : 'chat.rating.dislikePlaceholder')}
              rows={2}
              maxLength={4000}
            />
          </label>

          {error && <div className="rating-error" role="alert">{t('chat.rating.saveFailed')}</div>}

          <div className="rating-panel-foot">
            <button type="button" className="rating-remove" onClick={() => void remove()} disabled={saving}>
              {t('chat.rating.remove')}
            </button>
            <button type="button" className="rating-send" onClick={() => void send()} disabled={saving}>
              {saving ? t('chat.rating.saving') : t('chat.rating.send')}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const thumbLabel = (r: Rating) =>
    rating === r ? t('chat.rating.edit') : t(r === 'like' ? 'chat.rating.like' : 'chat.rating.dislike');

  return (
    <Popover
      content={panel}
      open={open}
      trigger={[]}
      placement={mirrorPlacement(direction, 'topLeft')}
      overlayClassName="rating-popover-overlay"
      arrow={false}
      destroyOnHidden
    >
      <div className="message-rating-buttons" ref={anchorRef}>
        {(['like', 'dislike'] as const).map((r) => (
          <Tooltip
            key={r}
            title={thumbLabel(r)}
            open={!open && hovered === r}
            overlayClassName="message-action-tooltip"
          >
            <button
              ref={thumbRefs[r]}
              type="button"
              className={`message-action-btn rating-thumb ${rating === r ? (r === 'like' ? 'liked' : 'disliked') : ''}`}
              onMouseEnter={() => setHovered(r)}
              onMouseLeave={() => setHovered((h) => (h === r ? null : h))}
              onClick={() => { setHovered(null); void chooseRating(r); }}
              disabled={saving && !open}
              aria-label={thumbLabel(r)}
              aria-pressed={rating === r}
              aria-haspopup="dialog"
              aria-expanded={open && rating === r}
            >
              <ThumbIcon down={r === 'dislike'} filled={rating === r} />
            </button>
          </Tooltip>
        ))}
      </div>
    </Popover>
  );
}
